import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as Icons from "lucide-react";
import { useFormContext } from "react-hook-form";
import FilterProvider, {
  FilterState,
} from "./(templates)/(context)/filter";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import React from "react";
import CreateReportTemplate from "@/components/features/reports/templates/create";
import EditReportTemplate from "@/components/features/reports/templates/edit";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import DeleteReportTemplate from "@/components/features/reports/templates/delete";
import { formatDate } from "@/lib/date";
import { useAuth } from "@/hooks/use-auth";
import { hasRight } from "@/config/rights.config";

type Props = {
  onRefetchReady?: (fn: () => void) => void;
};

const Templates = ({ onRefetchReady }: Props) => {
  const { user } = useAuth()
  const { trpc } = useTRPC();
  const { watch, setValue, register } = useFormContext<FilterState>();
  const filterState = watch();

  const query = useQuery({
    queryKey: ["report-templates", filterState],
    queryFn: async () => {
      const templates = await trpc.reports.templates.search.query({
        search: filterState.search,
      });
      return templates;
    },
    enabled: false,
  });

  React.useEffect(() => {
    onRefetchReady?.(() => query.refetch());
  }, [query.refetch]);

  React.useEffect(() => {
    query.refetch();
  }, [filterState]);

  return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between h-full gap-4">
        <div className="flex items-center justify-between gap-4">
          <Input
            {...register('search')}
            placeholder="Zoek naar sjabloon"
            startContent={<Icons.Search className="w-4 h-4" />}
            className="w-fit"
          />

          {hasRight(user?.role?.name, "REPORTS.WRITE") &&
            <CreateReportTemplate
              onCreated={() => query.refetch()}
              trigger={
                <Button
                  variant='bright'
                >
                  <Icons.Plus className="w-4 h-4" />
                  Nieuw sjabloon
                </Button>
              }
            />
          }
        </div>
        <div className="bg-foreground/10 rounded-md border p-2 overflow-auto h-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Type rapport</TableHead>
                <TableHead>Laatst gewijzigd door</TableHead>
                <TableHead>Laatst gewijzigd</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data?.reports?.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <EditReportTemplate
                      report_template_id={template.id}
                      onUpdated={() => {
                            query.refetch();
                          }}
                      trigger={
                        <Button variant="link" className="px-0">
                          {template.name}
                        </Button>
                      }
                    />
                  </TableCell>
                  <TableCell>{template.type?.join(', ')}</TableCell>
                  <TableCell>{template.editor?.first_name} {template.editor?.last_name}</TableCell>
                  <TableCell>{formatDate(template.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <Icons.MoreVertical className="w-4 h-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <EditReportTemplate
                            report_template_id={template.id}
                          onUpdated={() => {
                            query.refetch();
                          }}
                          trigger={
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                              <Icons.Pencil className="w-4 h-4" />
                              Bewerken
                            </DropdownMenuItem>
                          }
                          />

                          <DropdownMenuSeparator />

                          <DeleteReportTemplate
                            onDeleted={() => {
                              query.refetch();
                            }}
                            report_template_id={template.id}
                            trigger={
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive hover:!text-destructive/80">
                                <Icons.Trash className="w-4 h-4 text-destructive" />
                                Verwijderen
                              </DropdownMenuItem>
                            }
                          />
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total} rijen gevonden
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setValue("page", 1)}
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setValue("page", Math.max(1, Number(filterState.page) - 1))
              }
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              value={filterState.page}
              onChange={(e) => {
                setValue("page", e.currentTarget.valueAsNumber);
              }}
              onBlur={() => {
                query.refetch();
              }}
              type="number"
              min={1}
              max={query?.data?.pages || 1}
            />
            <Button
              variant="outline"
              onClick={() =>
                setValue(
                  "page",
                  Math.min(
                    Number(query?.data?.pages || 1),
                    Number(filterState.page) + 1
                  )
                )
              }
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", query?.data?.pages || 1)}
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ({ ...props }: Props) => {
  return (
    <FilterProvider>
      <Templates {...props} />
    </FilterProvider>
  );
};
